import * as React from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Stack,
  Tooltip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { createStyles } from "@mui/styles";
import BackSvg from "../assets/svg/ic_back.svg";
import useClient from "../hooks/useClient";
import { fCapitalizeFirst } from "../utils/formatString";
import Page from "./Page";
import { useData } from "../contexts/DataContext";
import { accessibleFeatures } from "../utils/rolesAccessMapping";

const useStyles = createStyles((theme) => ({
  rootBox: {
    margin: "3% 0%",
    justifyContent: "space-between",
    alignItems: "center",
    display: "flex",
    width: "100%"
  },
  title: {
    color: theme.palette.text.primary,
    fontFamily: "Inter",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: "normal",
    whiteSpace: "nowrap"
  },
  secondaryTitle: {
    color: theme.palette.primary,
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: "600",
    lineHeight: "normal",
    padding: "4px 12px",
    borderRadius: "12px",
    background: theme.palette.secondary.main,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "auto",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      padding: "4px 8px",
      maxWidth: "100px"
    }
  }
}));

PageHeader.propTypes = {
  primaryTitle: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  showSecondaryTitle: PropTypes.bool,
  showBack: PropTypes.bool,
  onClickButton: PropTypes.func,
  onClickBack: PropTypes.func,
  rightView: PropTypes.node,
  buttonStyle: PropTypes.object,
  isDisabled: PropTypes.bool,
  isPricingDisabled: PropTypes.bool
};

export default function PageHeader({
  client_name,
  page,
  primaryTitle,
  buttonText,
  showSecondaryTitle = true,
  showBack = false,
  onClickButton,
  onClickBack,
  rightView,
  buttonStyle,
  isDisabled,
  isPricingDisabled,
  screen
}) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();

  const { userData } = useData();
  const { selectedClient } = useClient();

  const onClickBackIcon = () => {
    navigate(-1);
  };

  function getClientName() {
    return fCapitalizeFirst(selectedClient?.name);
  }

  // Permission Mapping for Buttons
  const buttonPermissions = {
    "Add Client": accessibleFeatures.buttons.add.addClient,
    "Add Budget": accessibleFeatures.buttons.add.addBudget,
    "Add Estimation": accessibleFeatures.buttons.add.addEstimation,
    "Add Pricing": accessibleFeatures.buttons.add.addPricing,
    "Add Requisition": accessibleFeatures.buttons.add.addRequisition,
    "Add Contract": accessibleFeatures.buttons.add.addContract,
    "Add Approval": accessibleFeatures.buttons.add.addApproval,
    "Add Milestone": accessibleFeatures.buttons.add.addMilestone,
    "Add Schedule": accessibleFeatures.buttons.add.addSchedule,

    "Add Allocation": accessibleFeatures.buttons.add.addAllocation,
    "Add Skill-Payrate": accessibleFeatures.buttons.add.addSkillPayrate,
    "Add Customer": accessibleFeatures.buttons.add.addCustomer,
    "Add Employee": accessibleFeatures.buttons.add.addEmployee,
    "Add Software Asset": accessibleFeatures.buttons.add.addAsset,
    "Add Hardware Asset": accessibleFeatures.buttons.add.addAsset,
    "Generate Invoice": accessibleFeatures.buttons.add.generateInvoice,

    "Generate Actual": accessibleFeatures.buttons.add.generateActual,

    "Add Template": accessibleFeatures.buttons.add.addInvoiceTemplate,
    "Edit Contract": accessibleFeatures.buttons.edit.editContracts,
    "Edit Approval": accessibleFeatures.buttons.edit.editContracts,
    "Edit Milestone": accessibleFeatures.buttons.edit.editMilestone,
    "Edit Schedule": accessibleFeatures.buttons.edit.editMilestone,
    "Edit Asset": accessibleFeatures.buttons.edit.editMilestone,
    Edit: {
      Estimation: accessibleFeatures.buttons.edit.editEstimation,
      Pricing: accessibleFeatures.buttons.edit.editPricing,
      Contracts: accessibleFeatures.buttons.edit.editContracts,
      Client: accessibleFeatures.buttons.edit.editClient,
      Budget: accessibleFeatures.buttons.edit.editBudget,
      Allocation: accessibleFeatures.buttons.edit.editAllocation,
      Milestone: accessibleFeatures.buttons.edit.editMilestone,
      PO: accessibleFeatures.buttons.edit.editPO,
      SkillPayrate: accessibleFeatures.buttons.edit.editSkillPayrate,
      Customer: accessibleFeatures.buttons.edit.editCustomer,
      Employee: accessibleFeatures.buttons.edit.editEmployee
    }
  };

  const hasButtonPermission = () => {
    if (buttonText === "Edit" && screen) {
      // Check for Edit permissions based on the specific screen
      return userData?.user_roles?.some((role) =>
        buttonPermissions.Edit[screen]?.includes(role)
      );
    }

    if (Array.isArray(buttonPermissions[buttonText])) {
      return userData?.user_roles?.some((role) =>
        buttonPermissions[buttonText]?.includes(role)
      );
    }

    return false; // Default to no permission if buttonText does not match any key
  };
  const title = isDisabled
    ? "Contract is locked for this Estimation, You can't edit it"
    : isPricingDisabled
    ? "Contract is locked for this Pricing, You can't edit it"
    : null;

  // Render Button based on Permissions
  const renderButton = hasButtonPermission() ? (
    <Tooltip
      title={title}
      disableHoverListener={!isDisabled && !isPricingDisabled}
    >
      <span>
        <Button
          variant="contained"
          style={{ color: "black" }}
          onClick={onClickButton}
          sx={buttonStyle}
          disabled={isDisabled || isPricingDisabled}
        >
          {buttonText === "Add Allocation" ? "Add Placements" : buttonText}
        </Button>
      </span>
    </Tooltip>
  ) : null;

  return (
    <Page title={primaryTitle}>
      <Box sx={styles.rootBox}>
        <Stack spacing={2} direction="row">
          {showBack && (
            <img
              src={BackSvg}
              alt="BackIcon"
              style={{ cursor: "pointer" }}
              onClick={onClickBack ?? onClickBackIcon}
            />
          )}
          <Typography sx={styles.title}>{primaryTitle}</Typography>
          {showSecondaryTitle && (
            <Typography sx={styles.secondaryTitle}>
              {getClientName()}
            </Typography>
          )}
        </Stack>
        {rightView ? <>{rightView}</> : renderButton}
      </Box>
    </Page>
  );
}
